'use client';

import { AlertTriangle, Check, Copy, Loader2, Plus, Radio, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ConfirmationDialog } from '../../molecules/confirmation-dialog/confirmation-dialog';

/** POD visto nel feed MQTT di una configurazione, con l'ultima lettura. */
export interface MqttSeenPod {
	pod: string;
	/** ISO timestamp dell'ultimo campione ricevuto per questo POD */
	lastReadAt: string;
	/** Valore dell'ultimo campione (W per la potenza attiva) */
	value: number;
	/** Codice OBIS dell'ultimo campione */
	obis: string;
	/** true se nel tenant esiste un meter con questo codice POD */
	matched: boolean;
}

export interface MqttConfigItem {
	id: string;
	name: string;
	/** URL di ingest completo da incollare nel job n8n */
	url: string;
	lastReceivedAt: string | null;
	pods: MqttSeenPod[];
}

export interface MqttConfigsPanelProps {
	configs: MqttConfigItem[];
	/** POD censiti nel tenant che nessuna configurazione sta alimentando */
	metersWithoutData: { pod: string; kind: string }[];
	loading: boolean;
	onCreate: (name: string) => Promise<void>;
	onDelete: (configId: string) => Promise<void>;
	onRefresh: () => void;
}

/** OBIS della potenza attiva istantanea — l'unico reso come potenza in W. */
const OBIS_POWER_PREFIX = '1-0:1.7.0.255';

const INPUT_CLASS =
	'w-full rounded-[7px] border border-border bg-background px-2.5 py-1.5 text-[12px] text-text-primary outline-none transition-colors focus:border-accent';

function fmtAgo(iso: string): string {
	const diff = Date.now() - new Date(iso).getTime();
	if (diff < 90_000) return 'ora';
	if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min fa`;
	if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h fa`;
	return `${Math.floor(diff / 86_400_000)} g fa`;
}

function fmtValue(sample: MqttSeenPod): string {
	if (!sample.obis.startsWith(OBIS_POWER_PREFIX)) return String(sample.value);
	const watts = sample.value;
	if (Math.abs(watts) >= 1000) return `${(watts / 1000).toFixed(2)} kW`;
	return `${Math.round(watts)} W`;
}

/**
 * Gestione delle configurazioni di ingest MQTT a livello di configurazione
 * CER: creazione/revoca degli URL per i job n8n e confronto tra i POD
 * inoltrati dal broker (con ultima lettura) e i POD censiti — quelli non
 * censiti sono evidenziati. Presentazione pura, dati e azioni via props.
 */
export function MqttConfigsPanel({
	configs,
	metersWithoutData,
	loading,
	onCreate,
	onDelete,
	onRefresh,
}: MqttConfigsPanelProps) {
	const [copiedId, setCopiedId] = useState<string | null>(null);
	const [newName, setNewName] = useState('');
	const [creating, setCreating] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [pendingDelete, setPendingDelete] = useState<MqttConfigItem | null>(null);

	async function handleCopy(config: MqttConfigItem) {
		try {
			await navigator.clipboard.writeText(config.url);
			setCopiedId(config.id);
			setTimeout(() => setCopiedId(null), 2000);
		} catch {
			/* clipboard non disponibile (http/permessi) — l'URL resta selezionabile */
		}
	}

	async function handleCreate() {
		setCreating(true);
		try {
			await onCreate(newName.trim());
			setNewName('');
		} finally {
			setCreating(false);
		}
	}

	async function handleDelete(config: MqttConfigItem) {
		setDeletingId(config.id);
		try {
			await onDelete(config.id);
		} finally {
			setDeletingId(null);
		}
	}

	return (
		<div className="space-y-3 rounded-lg border border-border/70 bg-background/50 p-3">
			<ConfirmationDialog
				open={!!pendingDelete}
				onOpenChange={(open) => !open && setPendingDelete(null)}
				title="Eliminare la configurazione?"
				description={`«${pendingDelete?.name ?? ''}»: l'URL di ingest smetterà di funzionare per tutti i POD serviti dal job.`}
				confirmText="Elimina"
				isLoading={!!pendingDelete && deletingId === pendingDelete.id}
				onConfirm={async () => {
					if (pendingDelete) await handleDelete(pendingDelete);
				}}
			/>
			<div className="flex items-center gap-2">
				<Radio className="h-3 w-3 text-accent" />
				<span className="text-[11px] font-bold uppercase tracking-widest text-accent">
					Live Data (MQTT) · ingest
				</span>
				<button
					type="button"
					onClick={onRefresh}
					disabled={loading}
					title="Aggiorna"
					className="ml-auto rounded border border-border p-1.5 text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-40"
				>
					{loading ? (
						<Loader2 className="h-3 w-3 animate-spin" />
					) : (
						<RefreshCw className="h-3 w-3" />
					)}
				</button>
			</div>

			{configs.length === 0 && !loading && (
				<p className="text-[12px] text-text-muted">
					Nessuna configurazione di ingest: creane una per ottenere l'URL da incollare nel job MQTT
					n8n.
				</p>
			)}

			{configs.map((config) => {
				const missing = config.pods.filter((p) => !p.matched);
				return (
					<div
						key={config.id}
						className="space-y-2 rounded-md border border-border/60 bg-card/40 p-2.5"
					>
						<div className="flex items-center gap-2">
							<span className="text-[13px] font-semibold text-text-primary">{config.name}</span>
							<span className="text-[10px] text-text-muted">
								{config.lastReceivedAt
									? `Ultimo messaggio: ${fmtAgo(config.lastReceivedAt)}`
									: 'Nessun messaggio ricevuto'}
							</span>
							<button
								type="button"
								onClick={() => setPendingDelete(config)}
								disabled={deletingId === config.id}
								title="Elimina configurazione (revoca l'URL)"
								className="ml-auto rounded border border-danger/30 p-1.5 text-danger transition-colors hover:bg-danger/10 disabled:opacity-40"
							>
								{deletingId === config.id ? (
									<Loader2 className="h-3 w-3 animate-spin" />
								) : (
									<Trash2 className="h-3 w-3" />
								)}
							</button>
						</div>

						<div className="flex items-center gap-1.5">
							<input
								type="text"
								readOnly
								value={config.url}
								onFocus={(e) => e.currentTarget.select()}
								aria-label="URL della configurazione MQTT"
								className="w-full rounded-[7px] border border-border bg-background px-2.5 py-1.5 font-mono text-[12px] text-text-primary outline-none focus:border-accent"
							/>
							<button
								type="button"
								onClick={() => handleCopy(config)}
								title="Copia URL"
								className="rounded border border-border p-1.5 text-text-muted transition-colors hover:border-accent hover:text-accent"
							>
								{copiedId === config.id ? (
									<Check className="h-3 w-3 text-accent-green" />
								) : (
									<Copy className="h-3 w-3" />
								)}
							</button>
						</div>

						{config.pods.length === 0 ? (
							<p className="text-[11px] italic text-text-muted">
								Nessun POD ancora rilevato nel feed di questo job.
							</p>
						) : (
							<div className="space-y-1">
								<div className="flex items-center gap-2">
									<span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
										POD rilevati dal feed ({config.pods.length})
									</span>
									{missing.length > 0 && (
										<span className="rounded-full border border-danger/30 bg-danger/10 px-2 py-0.5 text-[10px] text-danger">
											{missing.length} non censiti
										</span>
									)}
								</div>
								{config.pods.map((p) => (
									<div
										key={p.pod}
										className={`flex flex-wrap items-center gap-2 rounded border px-2 py-1 ${
											p.matched
												? 'border-border/50 bg-background/40'
												: 'border-danger/40 bg-danger/5'
										}`}
									>
										<span className="font-mono text-[12px] text-text-primary">{p.pod}</span>
										{p.matched ? (
											<span className="inline-flex items-center gap-1 rounded-full border border-accent-green/30 bg-accent-green/10 px-1.5 py-0.5 text-[10px] text-accent-green">
												<Check className="h-2.5 w-2.5" /> censito
											</span>
										) : (
											<span className="inline-flex items-center gap-1 rounded-full border border-danger/30 bg-danger/10 px-1.5 py-0.5 text-[10px] text-danger">
												<AlertTriangle className="h-2.5 w-2.5" /> nessun POD corrispondente
											</span>
										)}
										<span className="ml-auto text-[11px] text-text-muted">
											Ultima lettura: {fmtAgo(p.lastReadAt)} ·{' '}
											<span className="text-text-primary">{fmtValue(p)}</span>
										</span>
									</div>
								))}
							</div>
						)}
					</div>
				);
			})}

			{configs.length > 0 && metersWithoutData.length > 0 && (
				<div className="space-y-1">
					<span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
						POD censiti senza dati MQTT ({metersWithoutData.length})
					</span>
					{metersWithoutData.map((m) => (
						<div
							key={`${m.pod}-${m.kind}`}
							className="flex items-center gap-2 rounded border border-warn/30 bg-warn/5 px-2 py-1"
						>
							<span className="font-mono text-[12px] text-text-primary">{m.pod}</span>
							<span className="text-[10px] uppercase text-text-muted">{m.kind}</span>
							<span className="ml-auto text-[11px] text-warn">nessun campione dal broker</span>
						</div>
					))}
				</div>
			)}

			<div className="flex items-center gap-1.5">
				<input
					type="text"
					value={newName}
					onChange={(e) => setNewName(e.target.value)}
					placeholder="Nome configurazione (es. Broker SGH sede)"
					className={INPUT_CLASS}
				/>
				<button
					type="button"
					onClick={handleCreate}
					disabled={creating}
					className="inline-flex shrink-0 items-center gap-1 rounded border border-accent bg-accent/10 px-2.5 py-1.5 text-[12px] font-semibold text-accent disabled:opacity-50"
				>
					{creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
					Nuova
				</button>
			</div>

			<p className="text-[10px] italic text-text-muted">
				Ogni configurazione corrisponde a un job n8n che inoltra in POST i messaggi MQTT all'URL
				indicato. Un messaggio può contenere più device: ogni entry viene instradata sul misuratore
				con il codice POD corrispondente; i POD del feed senza misuratore censito sono evidenziati
				in rosso.
			</p>
		</div>
	);
}
